import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus} from "./utils";
import {getLatestInvoiceNumber, getPrimaryBank} from "../components/Utility";

const initialState = {
    gstNumber: '',
    invoice: {},
    orderInvoice: {},
    primaryBank: {},
    enableDisable: {},
    allSetting: {},
    createGSTINStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    createPrimaryBankStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    createInvoiceSettingStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    createOrderInvoiceSettingStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    createAllToggleSettingStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchAllSettingStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    GSTINError: null,
    invoiceSettingError: null,
    orderInvoiceSettingError: null,
    primaryBankSettingError: null,
    toggleSettingError: null,
    allSettingError: null,
};

export const CreateGSTINNumber = createAsyncThunk('setting/CreateGSTINNumber', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/settings`, data);
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const CreateInvoiceSetting = createAsyncThunk('setting/CreateInvoiceSetting', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/invoice-counter/init`, data);
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const CreateOrderInvoiceSetting = createAsyncThunk('setting/CreateOrderInvoiceSetting', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/invoice-counter/orderInit`, data);
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const CreatePrimaryBank = createAsyncThunk('setting/CreatePrimaryBank', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/accounts/primary`, data);
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const CreateToggleSetting = createAsyncThunk('setting/CreateToggleSetting', async ({roleSign, data}) => {
    try {
        const response = await api.post(`/${roleSign}/settings/enable-disable`, data);
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const GetAllSetting = createAsyncThunk('setting/GetToggleSetting', async ({roleSign, scopeId}) => {
    try {
        const response = await api.get(`/${roleSign}/settings?id=${scopeId}`);
        return {...response}
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


const settingSlice = createSlice({
    name: "setting",
    initialState: initialState,
    reducers: {
        resetGSTCreateStatus(state) {
            state.createGSTINStatus = 'idle';
        },
        resetInvoiceSettingCreateStatus(state) {
            state.createInvoiceSettingStatus = 'idle';
        },
        resetOrderInvoiceSettingCreateStatus(state) {
            state.createOrderInvoiceSettingStatus = 'idle';
        },
        resetPrimaryBankSettingCreateStatus(state) {
            state.createPrimaryBankStatus = 'idle';
        },
        resetAllToggleSettingCreateStatus(state) {
            state.createAllToggleSettingStatus = 'idle';
        },
    },
    extraReducers(builder) {
        builder
            // Create GSTIN number
            .addCase(CreateGSTINNumber.pending, (state) => {
                state.createGSTINStatus = 'loading';
            })
            .addCase(CreateGSTINNumber.fulfilled, (state, action) => {
                const {createGSTINStatus, error} = handleResponseStatus(action.payload, 'createGSTINStatus');
                if (createGSTINStatus === "success") {
                    state.gstNumber = action.payload.data.data.gstNumber

                }
                state.createGSTINStatus = createGSTINStatus;
                state.GSTINError = error;
            })
            .addCase(CreateGSTINNumber.rejected, (state, action) => {
                state.createGSTINStatus = 'failed';
                state.GSTINError = action.error.message;
            })

            // Create Invoice setting
            .addCase(CreateInvoiceSetting.pending, (state) => {
                state.createInvoiceSettingStatus = 'loading';
            })
            .addCase(CreateInvoiceSetting.fulfilled, (state, action) => {
                const {
                    createInvoiceSettingStatus,
                    error
                } = handleResponseStatus(action.payload, 'createInvoiceSettingStatus');

                if (createInvoiceSettingStatus === "success") {
                    state.invoice = {
                        ...action.payload.data.data
                    };
                }
                state.createInvoiceSettingStatus = createInvoiceSettingStatus;
                state.invoiceSettingError = error;
            })
            .addCase(CreateInvoiceSetting.rejected, (state, action) => {
                state.createInvoiceSettingStatus = 'failed';
                state.invoiceSettingError = action.error.message;
            })

            // Create Order Invoice setting
            .addCase(CreateOrderInvoiceSetting.pending, (state) => {
                state.createOrderInvoiceSettingStatus = 'loading';
            })
            .addCase(CreateOrderInvoiceSetting.fulfilled, (state, action) => {
                const {
                    createOrderInvoiceSettingStatus,
                    error
                } = handleResponseStatus(action.payload, 'createOrderInvoiceSettingStatus');

                if (createOrderInvoiceSettingStatus === "success") {
                    state.orderInvoice = {
                        ...action.payload.data.data
                    };
                }
                state.createOrderInvoiceSettingStatus = createOrderInvoiceSettingStatus;
                state.orderInvoiceSettingError = error;
            })
            .addCase(CreateOrderInvoiceSetting.rejected, (state, action) => {
                state.createOrderInvoiceSettingStatus = 'failed';
                state.orderInvoiceSettingError = action.error.message;
            })

            // Create Primary bank
            .addCase(CreatePrimaryBank.pending, (state) => {
                state.createPrimaryBankStatus = 'loading';
            })
            .addCase(CreatePrimaryBank.fulfilled, (state, action) => {
                const {
                    createPrimaryBankStatus,
                    error
                } = handleResponseStatus(action.payload, 'createPrimaryBankStatus');

                if (createPrimaryBankStatus === "success") {
                    state.primaryBank = {
                        ...action.payload.data.data
                    };
                }
                state.createPrimaryBankStatus = createPrimaryBankStatus;
                state.primaryBankSettingError = error;
            })
            .addCase(CreatePrimaryBank.rejected, (state, action) => {
                state.createPrimaryBankStatus = 'failed';
                state.primaryBankSettingError = action.error.message;
            })

            // Create Toggle Setting
            .addCase(CreateToggleSetting.pending, (state) => {
                state.createAllToggleSettingStatus = 'loading';
            })
            .addCase(CreateToggleSetting.fulfilled, (state, action) => {
                const {
                    createAllToggleSettingStatus,
                    error
                } = handleResponseStatus(action.payload, 'createAllToggleSettingStatus');
                if (createAllToggleSettingStatus === "success") {
                    state.enableDisable = {
                        ...action.payload.data.data
                    };
                }
                state.createAllToggleSettingStatus = createAllToggleSettingStatus;
                state.toggleSettingError = error;
            })
            .addCase(CreateToggleSetting.rejected, (state, action) => {
                state.createAllToggleSettingStatus = 'failed';
                state.toggleSettingError = action.error.message;
            })

            // Get All settings
            .addCase(GetAllSetting.pending, (state) => {
                state.fetchAllToggleSettingStatus = 'loading';
            })
            .addCase(GetAllSetting.fulfilled, (state, action) => {
                const {
                    fetchAllSettingStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchAllSettingStatus');

                if (fetchAllSettingStatus === "success") {
                    const {data} = action.payload.data ?? {};

                    const primaryBank = getPrimaryBank(data?.bank);
                    const latestInvoice = getLatestInvoiceNumber(data?.invoiceCounter);
                    const latestOrderInvoice = getLatestInvoiceNumber(data?.orderInvoiceCounter);

                    state.allSetting = data ?? {};
                    state.enableDisable = data?.enableDisable ?? {};
                    state.gstNumber = data?.gstNumber ?? "";
                    state.primaryBank = primaryBank ?? {};
                    state.invoice = latestInvoice ?? {};
                    state.orderInvoice = latestOrderInvoice ?? {};
                }
                state.fetchAllSettingStatus = fetchAllSettingStatus;
                state.allSettingError = error;
            })
            .addCase(GetAllSetting.rejected, (state, action) => {
                state.fetchAllSettingStatus = 'failed';
                state.allSettingError = action.error.message;
            })
    }
});

export const {
    resetGSTCreateStatus,
    resetInvoiceSettingCreateStatus,
    resetPrimaryBankSettingCreateStatus,
    resetAllToggleSettingCreateStatus,
    resetOrderInvoiceSettingCreateStatus
} = settingSlice.actions;
export default settingSlice.reducer;


